import * as React from "react";
import { Suspense } from "react";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  StyledSubTabsBox,
  StyledSubTabsBarBox,
  StyledSubHeadingTabsGroup,
  StyledSubHeadingText,
  StyledSubHeaderButton,
  StyledSubTab,
  StyledSubTabsWrapper,
  StyledSubTabComponent,
} from "./CustomSubTabs.style";

const subTabKeys = INFRAMARK_CONSTANTS?.dataManagement?.subTabKeys ?? [];
const subTabNames = INFRAMARK_CONSTANTS?.subTabNames ?? {};

export const PlaceholderPanel = ({ label }) => (
  <Box sx={{ p: 2 }} data-testid={`subtab-content-${label.replace(/\s/g, "-").toLowerCase()}`}>
    {label} content
  </Box>
);

const defaultTabs = subTabKeys.map((key) => {
  const label = subTabNames[key] ?? key;
  return {
    label,
    component: () => <PlaceholderPanel label={label} />,
  };
});

function CustomSubTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`sub-tabpanel-${index}`}
      aria-labelledby={`sub-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `sub-tab-${index}`,
    "aria-controls": `sub-tabpanel-${index}`,
  };
}

export default function CustomSubTabs({
  headerTitle,
  tabs = defaultTabs,
  headerButton,
}) {
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <StyledSubTabsBox>
      <StyledSubTabsBarBox>
        <StyledSubHeadingTabsGroup>
          {headerTitle ? (
            <StyledSubHeadingText data-testid="custom-subtabs-header">
              {headerTitle}
            </StyledSubHeadingText>
          ) : null}
          <StyledSubTabsWrapper>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="sub-tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {tabs?.map((tab, index) => (
                <StyledSubTab
                  key={index}
                  label={tab?.label}
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          </StyledSubTabsWrapper>
        </StyledSubHeadingTabsGroup>
        {headerButton?.showButton && (
          <StyledSubHeaderButton
            variant="outlined"
            onClick={() => headerButton?.onClickHandler?.()}
          >
            {headerButton?.label}
          </StyledSubHeaderButton>
        )}
      </StyledSubTabsBarBox>
      <StyledSubTabComponent>
        {tabs?.map((tab, index) => {
          const Component = tab?.component;
          return (
            <CustomSubTabPanel key={index} value={value} index={index}>
              {Component && (
                <Suspense fallback={<div>Loading...</div>}>
                  <Component />
                </Suspense>
              )}
            </CustomSubTabPanel>
          );
        })}
      </StyledSubTabComponent>
    </StyledSubTabsBox>
  );
}

CustomSubTabs.propTypes = {
  headerTitle: PropTypes.string,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      component: PropTypes.node,
    })
  ),
  headerButton: PropTypes.shape({
    showButton: PropTypes.bool,
    label: PropTypes.string,
    onClickHandler: PropTypes.func,
  }),
};

CustomSubTabs.defaultProps = {
  headerTitle: "",
  tabs: defaultTabs,
  headerButton: {
    showButton: false,
  },
};

export { defaultTabs };
